import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/clients/overview`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [companyId, API]);

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!data) return <div style={pageStyle}><p>Failed to load client overview.</p></div>;

  const s = data.summary || {};

  const statusCards = [
    { label: 'Total Clients', value: s.total_clients, color: '#3b82f6' },
    { label: 'Active', value: s.active_clients, color: '#10b981' },
    { label: 'Prospects', value: s.prospects, color: '#8b5cf6' },
    { label: 'On Hold', value: s.on_hold, color: '#f59e0b' },
    { label: 'Inactive', value: s.inactive, color: '#6b7280' },
    { label: 'Archived', value: s.archived, color: '#94a3b8' },
  ];

  const healthCards = [
    { label: 'Good', value: s.health_good, color: '#22c55e' },
    { label: 'Attention Needed', value: s.health_attention, color: '#f59e0b' },
    { label: 'At Risk', value: s.health_at_risk, color: '#ef4444' },
  ];

  const metricCards = [
    { label: 'Total Contacts', value: s.total_contacts, color: '#06b6d4' },
    { label: 'Active Services', value: s.active_services, color: '#3b82f6' },
    { label: 'New Clients (30d)', value: s.new_clients, color: '#8b5cf6' },
  ];

  const healthColor = { good: '#22c55e', attention_needed: '#f59e0b', at_risk: '#ef4444' };
  const statusColor = { prospect: '#8b5cf6', active: '#10b981', on_hold: '#f59e0b', inactive: '#6b7280', archived: '#94a3b8' };

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem', fontWeight: 700 }}>👥 Client Overview</h2>

      {/* Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {statusCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: c.color }}>{c.value || 0}</div>
          </div>
        ))}
      </div>

      {/* Health & Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {healthCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Health: {c.label}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: c.color }}>{c.value || 0}</div>
          </div>
        ))}
        {metricCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: c.color }}>{c.value || 0}</div>
          </div>
        ))}
      </div>

      {/* Pending Actions */}
      {Array.isArray(data.pending_actions) && data.pending_actions.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '20px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>⚠️ Pending Actions</h3>
          {data.pending_actions.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#334155' }}>{a.label}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ef4444' }}>{a.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Top Clients */}
      {Array.isArray(data.top_clients) && data.top_clients.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>🏆 Top Active Clients</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Health</th>
                  <th style={thStyle}>Owner</th>
                  <th style={thStyle}>Services</th>
                  <th style={thStyle}>Contacts</th>
                </tr>
              </thead>
              <tbody>
                {data.top_clients.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/clients/${c.id}`)}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{c.display_name || c.name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{c.client_code}</div>
                    </td>
                    <td style={tdStyle}>{c.client_type?.name || '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: (healthColor[c.health] || '#6b7280') + '20', color: healthColor[c.health] || '#6b7280' }}>
                        {(c.health || 'good').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>{c.account_owner?.name || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{c.active_services_count || 0}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{c.contacts_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {Array.isArray(data.recent_activity) && data.recent_activity.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📋 Recent Activity</h3>
          {data.recent_activity.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: a.importance === 'important' ? '#f59e0b' : a.importance === 'critical' ? '#ef4444' : '#3b82f6', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', color: '#334155' }}>{a.description}</div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  {a.user?.name || 'System'} • {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
